import React, { useState, useEffect, useCallback, useRef } from 'react'
import { designTokens } from '@/utils/design-tokens'
import Button from '@/components/ui/Button'
import { Search, X } from 'lucide-react'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { AcademyListItem } from '@/api/generated/models/AcademyListItem'

interface SchoolSettingModalProps {
  isOpen: boolean
  onClose: () => void
  orgId: string
  currentMyAcademy: AcademyListItem | null
  currentCompareAcademies: AcademyListItem[]
  onSaved: () => void
}

const MAX_COMPARE = 6

const SchoolSettingModal: React.FC<SchoolSettingModalProps> = ({
  isOpen,
  onClose,
  orgId,
  currentMyAcademy,
  currentCompareAcademies,
  onSaved,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<AcademyListItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedCompareAcademies, setSelectedCompareAcademies] = useState<AcademyListItem[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const selectedCompareIds = selectedCompareAcademies.map(a => a.id)

  // Init local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCompareAcademies([...currentCompareAcademies])
      setSearchQuery('')
      setSearchResults([])
    }
  }, [isOpen, currentCompareAcademies])

  // Search academies with debounce
  const searchAcademies = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    setIsSearching(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsListAcademies({
        search: query,
        perPage: 30,
      })
      setSearchResults(response.data || [])
    } catch (err) {
      console.error('Failed to search academies:', err)
    } finally {
      setIsSearching(false)
    }
  }, [])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchAcademies(value), 300)
  }

  const toggleCompareSchool = (academy: AcademyListItem) => {
    if (academy.id === currentMyAcademy?.id) return

    setSelectedCompareAcademies(prev => {
      if (prev.some(a => a.id === academy.id)) {
        return prev.filter(a => a.id !== academy.id)
      }
      if (prev.length >= MAX_COMPARE) return prev
      return [...prev, academy]
    })
  }

  const removeCompareSchool = (academyId: string) => {
    setSelectedCompareAcademies(prev => prev.filter(a => a.id !== academyId))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await AcademyinfoService.appsAcademyinfoViewsUpdateCompareAcademies({
        orgId,
        requestBody: {
          compare_academy_ids: selectedCompareIds,
        },
      })
      onSaved()
    } catch (err) {
      console.error('Failed to save setting:', err)
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl flex flex-col" style={{ width: '560px', maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
          <h2 style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '18px', fontWeight: 700, color: designTokens.colors.gray[6] }}>
            설정
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: designTokens.colors.gray[4] }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* My School */}
          {currentMyAcademy && (
            <div className="mb-4">
              <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 600, color: designTokens.colors.gray[5] }}>
                우리학교
              </span>
              <div
                className="mt-1 px-3 py-2 rounded"
                style={{ backgroundColor: designTokens.colors.primary.DEFAULT + '15', border: `1px solid ${designTokens.colors.primary.DEFAULT}` }}
              >
                <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', fontWeight: 600, color: designTokens.colors.primary.DEFAULT }}>
                  {currentMyAcademy.name}
                </span>
                <span style={{ fontSize: '12px', color: designTokens.colors.gray[4], marginLeft: '8px' }}>
                  {currentMyAcademy.establishment_type} / {currentMyAcademy.region}
                </span>
              </div>
            </div>
          )}

          {/* Selected Compare Schools */}
          <div className="mb-3">
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 600, color: designTokens.colors.gray[5] }}>
              비교학교 ({selectedCompareIds.length}/{MAX_COMPARE})
            </span>
            {selectedCompareAcademies.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {selectedCompareAcademies.map(academy => (
                  <div
                    key={academy.id}
                    className="flex items-center gap-1 px-2.5 py-1 rounded"
                    style={{ backgroundColor: designTokens.colors.secondary.DEFAULT, color: '#fff', fontSize: '13px', fontFamily: designTokens.typography.fontFamily.sans[0] }}
                  >
                    {academy.name}
                    <button
                      onClick={() => removeCompareSchool(academy.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: 0 }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="mb-3">
            <div
              className="flex items-center gap-2 px-3 py-2 rounded border"
              style={{ borderColor: designTokens.colors.gray[2] }}
            >
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: designTokens.colors.gray[4] }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="학교명으로 검색"
                className="flex-1 outline-none bg-transparent"
                style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[6], border: 'none' }}
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setSearchResults([]) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: designTokens.colors.gray[4] }}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results */}
          {isSearching ? (
            <div className="text-center py-4">
              <span style={{ fontSize: '13px', color: designTokens.colors.gray[4] }}>검색 중...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {searchResults.map(academy => {
                const isMySchool = academy.id === currentMyAcademy?.id
                const isSelected = selectedCompareIds.includes(academy.id)
                const isDisabled = isMySchool || (selectedCompareIds.length >= MAX_COMPARE && !isSelected)

                return (
                  <button
                    key={academy.id}
                    onClick={() => !isDisabled && toggleCompareSchool(academy)}
                    disabled={isDisabled}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '13px',
                      fontWeight: isSelected ? 600 : 400,
                      color: isMySchool
                        ? designTokens.colors.primary.DEFAULT
                        : isSelected ? '#fff' : designTokens.colors.gray[6],
                      backgroundColor: isMySchool
                        ? designTokens.colors.primary.DEFAULT + '15'
                        : isSelected
                          ? designTokens.colors.secondary.DEFAULT
                          : designTokens.colors.gray[0],
                      border: `1px solid ${isMySchool
                        ? designTokens.colors.primary.DEFAULT
                        : isSelected
                          ? designTokens.colors.secondary.DEFAULT
                          : designTokens.colors.gray[2]}`,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled && !isMySchool ? 0.5 : 1,
                    }}
                  >
                    {academy.name}
                    {isMySchool && (
                      <span style={{ fontSize: '11px', marginLeft: '4px' }}>(우리학교)</span>
                    )}
                  </button>
                )
              })}
            </div>
          ) : searchQuery.trim() ? (
            <div className="text-center py-4">
              <span style={{ fontSize: '13px', color: designTokens.colors.gray[4] }}>검색 결과가 없습니다.</span>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 flex-shrink-0" style={{ borderTop: `1px solid ${designTokens.colors.gray[1]}` }}>
          <Button size="small" color="white" onClick={onClose} disabled={isSaving}>
            취소
          </Button>
          <Button size="small" color="dark" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중...' : '확인'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SchoolSettingModal
